import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Box,
  Typography,
  Divider,
  Link,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { fetchUpdatePassword } from 'src/redux/candidate'
import { useGlobalNavigationBlock } from './useGlobalNavigationBlock'

const PasswordSection = ({ candidate }) => {
  const dispatch = useDispatch()
  const { id } = useSelector(state => state.candidate)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    password: '',
    confirmPassword: '',
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  })
  const [errors, setErrors] = useState({})
  const [hasChanges, setHasChanges] = useState(false)
  useGlobalNavigationBlock(hasChanges)

  const setDefaultValues = () => {
    if (candidate?.errors && Object.keys(candidate.errors).length > 0) {
      const newErrors = {}
      Object.keys(candidate.errors).forEach(key => {
        newErrors[key] = candidate.errors[key][0]
      })
      setErrors(newErrors)
    }
  }

  React.useEffect(() => {
    if (candidate && !candidate.loading) {
      setDefaultValues()
    }
  }, [candidate])

  const handleInputChange = event => {
    const { name, value } = event.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
    setHasChanges(true)
  }

  const toggleShowPassword = field => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
  }

  const handleSave = async () => {
    if (Object.keys(errors).length > 0) return

    try {
      await dispatch(
        fetchUpdatePassword({
          currentPassword: passwordData.currentPassword,
          password: passwordData.password,
          confirmPassword: passwordData.confirmPassword,
          id: id,
        }),
      )

      // Clear password fields on successful submit
      setPasswordData({
        currentPassword: '',
        password: '',
        confirmPassword: '',
      })
      setHasChanges(false)
    } catch (error) {
      // Errors will be handled by Redux and reflected in the candidate state
      console.error('Password update failed:', error)
    }
  }

  const allFieldsFilled =
    passwordData.currentPassword && passwordData.password && passwordData.confirmPassword

  return (
    <>
      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Current Password"
          type={showPasswords.current ? 'text' : 'password'}
          name="currentPassword"
          value={passwordData.currentPassword}
          onChange={handleInputChange}
          error={!!errors.currentPassword}
          helperText={errors.currentPassword}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleShowPassword('current')}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="New Password"
          type={showPasswords.new ? 'text' : 'password'}
          name="password"
          value={passwordData.password}
          onChange={handleInputChange}
          error={!!errors.password}
          helperText={errors.password || 'Must be at least 8 characters'}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleShowPassword('new')}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirm New Password"
          type={showPasswords.confirm ? 'text' : 'password'}
          name="confirmPassword"
          value={passwordData.confirmPassword}
          onChange={handleInputChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          variant="outlined"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleShowPassword('confirm')}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Button
        variant='contained'
        color='primary'
        onClick={handleSave}
        disabled={!hasChanges || Object.keys(errors).length > 0 || !allFieldsFilled}
        sx={{ mt: 3 }}
      >
        Save New Password
      </Button>

      {/* Profile Deletion Notice */}
      <Divider sx={{ mt: 4, mb: 3 }} />
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          If you want to delete your profile, please contact us at{' '}
          <Link href="mailto:contact@mystethi.com" underline="hover">
            contact@mystethi.com
          </Link>
        </Typography>
      </Box>
    </>
  )
}

export default PasswordSection
